import { FC, useState } from 'react';

import { getLogger } from '@ravago/shared/logging/client';
import { UserUtils } from '@ravago/shared/next-data-access/client';
import { EmailFormValue, RegistrationStep } from '@ravago/shared/page-data/models/elements';
import { Alert } from '@ravago/shared/radiance/components/alert/components/Alert/Alert';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { Link } from '@ravago/shared/radiance/components/link/components/Link/Link';
import { useTranslations } from '@ravago/shared/translations/client';

import { Controller, useForm } from 'react-hook-form';

import { EMAIL_VALIDATION_REGEX } from '../../../../utils/validation/email-validation';

interface EmailAddressStepProps {
   defaultFormValue: EmailFormValue | undefined;
   handleSubmitEmailForm: (formValue: EmailFormValue) => void;
   handleChangeStep: (step: RegistrationStep) => void;
   handleLogin: () => void;
   supportEmail?: string;
}

export const EmailAddressStep: FC<EmailAddressStepProps> = ({ defaultFormValue, handleSubmitEmailForm, handleChangeStep, handleLogin, supportEmail }) => {
   const translate = useTranslations();

   const [showAlreadyInUseMsg, setShowAlreadyInUseMsg] = useState(false);
   const [showRegistrationAlert, setShowRegistrationAlert] = useState(false);

   const getDefaultFormValue = () => ({
      email: ''
   });

   const { control, handleSubmit } = useForm<EmailFormValue, unknown, EmailFormValue>({
      defaultValues: defaultFormValue ?? getDefaultFormValue()
   });

   const handleSubmitForm = async () => {
      setShowRegistrationAlert(false);

      try {
         await handleSubmit(async (value) => {
            const exists = await UserUtils.checkIfUserExists(value.email);

            setShowAlreadyInUseMsg(exists);

            if (!exists) {
               handleSubmitEmailForm(value);
               handleChangeStep(RegistrationStep.ACCOUNT_INFO);
            }
         })();
      } catch (error) {
         getLogger().error(error instanceof Error ? error.message : 'Unknown error occurred');

         setShowRegistrationAlert(true);
      }
   };

   return (
      <Box direction="column" gap="lg">
         {showAlreadyInUseMsg && (
            <Alert headline={translate('auth.email-already-in-use-alert-title')} variant="neutral">
               {translate('auth.email-already-in-use-alert-description')}
               { }
               <Link onClick={handleLogin}>{translate('auth.email-already-in-use-alert-description-login')}</Link>
            </Alert>
         )}
         {showRegistrationAlert && (
            <Alert headline={translate('auth.registration-alert-title')} variant="error">
               {translate('auth.registration-alert-description')}
               {supportEmail && (
                  <>
                     {' '}
                     <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link>.
                  </>
               )}
            </Alert>
         )}

         <Controller
            control={control}
            name="email"
            rules={{
               required: translate('form-validation.required', { field: translate('auth.company-email-address') }),
               maxLength: {
                  value: 40,
                  message: translate('form-validation.max-length', { field: translate('auth.company-email-address') })
               },
               pattern: {
                  value: EMAIL_VALIDATION_REGEX,
                  message: translate('form-validation.invalid-email')
               }
            }}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
               <InputField
                  inputRef={ref}
                  {...field}
                  id="registration-email"
                  label={translate('auth.company-email-address')}
                  required
                  type="text"
                  error={!!error}
                  errorMessage={error?.message}
               />
            )}
         />
         <Box justify="end">
            <Button onClick={() => handleSubmitForm()} disabled={showRegistrationAlert}>
               {translate('auth.next-step')}
               <Icon icon="arrow-right" />
            </Button>
         </Box>
      </Box>
   );
};
